import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Input, message, Space, Popconfirm, Card, Row, Col, Select, AutoComplete } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MedicineBoxOutlined } from '@ant-design/icons';
import { doctorService } from '../../services';
import SearchFilter from '../../components/SearchFilter';
import FormModal from '../../components/FormModal';
import { useTranslation } from 'react-i18next';
import DataTransfer from '../../components/DataTransfer';
import './Drugs.css';

const Drugs = () => {
    const [drugs, setDrugs] = useState([]);
    const [filteredDrugs, setFilteredDrugs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingDrug, setEditingDrug] = useState(null);
    const [form] = Form.useForm();
    const { t } = useTranslation();

    useEffect(() => {
        fetchDrugs();
    }, []);

    const fetchDrugs = async () => {
        setLoading(true);
        try {
            const response = await doctorService.getDrugs();
            setDrugs(response.data);
            setFilteredDrugs(response.data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (values) => {
        let filtered = drugs;

        if (values.name) {
            filtered = filtered.filter(d =>
                d.name.toLowerCase().includes(values.name.toLowerCase()) ||
                (d.genericName && d.genericName.toLowerCase().includes(values.name.toLowerCase()))
            );
        }

        if (values.dosageForm) {
            filtered = filtered.filter(d => d.dosageForm === values.dosageForm);
        }

        if (values.category) {
            filtered = filtered.filter(d => d.category === values.category);
        }

        setFilteredDrugs(filtered);
    };

    const handleReset = () => {
        setFilteredDrugs(drugs);
    };

    const handleCreate = () => {
        setEditingDrug(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingDrug(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await doctorService.deleteDrug(id);
            message.success(t('success'));
            fetchDrugs();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingDrug) {
                await doctorService.updateDrug(editingDrug._id, values);
                message.success(t('success'));
            } else {
                // The list holds one row per drug, so a name that is already there
                // (typed into a prescription earlier, say) is not added twice - say
                // so rather than let it look like a new row was created.
                const response = await doctorService.createDrug(values);
                if (response.created === false) {
                    message.info(t('drugAlreadyInList'));
                } else {
                    message.success(t('success'));
                }
            }
            setModalVisible(false);
            form.resetFields();
            fetchDrugs();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const columns = [
        {
            title: t('drugName'),
            dataIndex: 'name',
            key: 'name',
            render: (name) => (
                <Space>
                    <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                    <strong>{name}</strong>
                </Space>
            )
        },
        {
            title: t('genericName'),
            dataIndex: 'genericName',
            key: 'genericName',
            render: (text) => text || '-'
        },
        {
            title: t('category'),
            dataIndex: 'category',
            key: 'category',
            render: (text) => text || '-'
        },
        {
            title: t('dosageForm'),
            dataIndex: 'dosageForm',
            key: 'dosageForm',
            render: (text) => text || '-'
        },
        {
            title: t('strength'),
            dataIndex: 'strength',
            key: 'strength',
            render: (text) => text || '-'
        },
        // What the drug is usually prescribed at. Filled in from the first
        // prescription that carried it, and shown in the prescription search - so
        // a value that reads wrong here is worth correcting.
        {
            title: t('usualDosage'),
            dataIndex: 'defaultDosage',
            key: 'defaultDosage',
            render: (text) => text || '-'
        },
        {
            title: t('usualFrequency'),
            dataIndex: 'defaultFrequency',
            key: 'defaultFrequency',
            render: (text) => text || '-'
        },
        {
            title: t('usualDuration'),
            dataIndex: 'defaultDuration',
            key: 'defaultDuration',
            render: (text) => text || '-'
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Popconfirm
                        title={t('confirmDelete')}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        <Button icon={<DeleteOutlined />} danger size="small" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="page-container">
            <Row gutter={[16, 16]} align="middle" justify="space-between" className="page-header">
                <Col xs={24} sm={16} md={12}>
                    <h1 className="page-title">{t('drugs')}</h1>
                </Col>
                <Col xs={24} sm={8} md={12} style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                        size="large"
                    >
                        {t('addDrug')}
                    </Button>
                </Col>
                <Col xs={24}>
                    <DataTransfer section="drugs" size="small" onImported={fetchDrugs} />
                </Col>
            </Row>

            <Card className="search-filter-card">
                <SearchFilter
                    searchFields={[
                        { key: 'name', label: t('drugName'), placeholder: t('searchBy') + ' ' + t('drugName') }
                    ]}
                    filterFields={[
                        {
                            key: 'dosageForm',
                            label: t('dosageForm'),
                            options: [
                                { label: 'Tablet', value: 'Tablet' },
                                { label: 'Capsule', value: 'Capsule' },
                                { label: 'Liquid', value: 'Liquid' },
                                { label: 'Injection', value: 'Injection' },
                                { label: 'Cream', value: 'Cream' },
                                { label: 'Other', value: 'Other' }
                            ]
                        },
                        {
                            key: 'category',
                            label: t('familyCategory'),
                            options: [
                                { label: 'Antibiotics', value: 'Antibiotics' },
                                { label: 'Painkillers', value: 'Painkillers' },
                                { label: 'Antihistamines', value: 'Antihistamines' },
                                { label: 'Vitamins', value: 'Vitamins' },
                                { label: 'Other', value: 'Other' }
                            ]
                        }
                    ]}
                    onSearch={handleSearch}
                    onReset={handleReset}
                />
            </Card>

            <Card className="table-card">
                <Table
                    columns={columns}
                    dataSource={filteredDrugs}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 1200 }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total')}: ${total}`,
                    }}
                />
            </Card>

            <FormModal
                title={editingDrug ? t('edit') + ' ' + t('drugs') : t('addDrug')}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onSubmit={handleSubmit}
                form={form}
                width={600}
                className="form-modal-two-column"
            >
                <Form.Item
                    name="name"
                    label={t('drugName')}
                    rules={[{ required: true, message: t('drugName') + ' ' + t('required') }]}
                >
                    <Input placeholder="e.g., Aspirin" />
                </Form.Item>

                <Form.Item name="genericName" label={t('genericName')}>
                    <Input placeholder="e.g., Acetylsalicylic Acid" />
                </Form.Item>

                <Form.Item name="category" label={t('familyCategory')}>
                    <AutoComplete
                        placeholder="Select or type a category"
                        options={[
                            { label: 'Antibiotics', value: 'Antibiotics' },
                            { label: 'Painkillers', value: 'Painkillers' },
                            { label: 'Antihistamines', value: 'Antihistamines' },
                            { label: 'Vitamins', value: 'Vitamins' },
                            { label: 'Antacids', value: 'Antacids' },
                            { label: 'Antidiabetics', value: 'Antidiabetics' },
                            { label: 'Antihypertensives', value: 'Antihypertensives' },
                            { label: 'Steroids', value: 'Steroids' },
                            { label: 'Anticoagulants', value: 'Anticoagulants' },
                            { label: 'Other', value: 'Other' }
                        ]}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>

                <Form.Item name="dosageForm" label={t('dosageForm')}>
                    <AutoComplete
                        placeholder="Select or type a dosage form"
                        options={[
                            { label: 'Tablet', value: 'Tablet' },
                            { label: 'Capsule', value: 'Capsule' },
                            { label: 'Liquid', value: 'Liquid' },
                            { label: 'Syrup', value: 'Syrup' },
                            { label: 'Injection', value: 'Injection' },
                            { label: 'Cream', value: 'Cream' },
                            { label: 'Ointment', value: 'Ointment' },
                            { label: 'Drops', value: 'Drops' },
                            { label: 'Inhaler', value: 'Inhaler' },
                            { label: 'Powder', value: 'Powder' },
                            { label: 'Other', value: 'Other' }
                        ]}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>

                <Form.Item name="strength" label={t('strength')}>
                    <Input placeholder="e.g., 100mg, 500mg" />
                </Form.Item>

                <Form.Item
                    label={t('usualPrescription')}
                    help={t('usualPrescriptionHint')}
                    className="ant-form-item-full"
                >
                    <Space.Compact style={{ width: '100%' }}>
                        <Form.Item name="defaultDosage" noStyle>
                            <Input style={{ width: '34%' }} placeholder={t('usualDosage')} />
                        </Form.Item>
                        <Form.Item name="defaultFrequency" noStyle>
                            <Input style={{ width: '33%' }} placeholder={t('usualFrequency')} />
                        </Form.Item>
                        <Form.Item name="defaultDuration" noStyle>
                            <Input style={{ width: '33%' }} placeholder={t('usualDuration')} />
                        </Form.Item>
                    </Space.Compact>
                </Form.Item>

                <Form.Item
                    name="description"
                    label={t('description')}
                    className="ant-form-item-full"
                >
                    <Input.TextArea
                        rows={3}
                        placeholder="Additional information about the drug"
                    />
                </Form.Item>
            </FormModal>
        </div>
    );
};

export default Drugs;
